// One question, rendered according to its type.
//
// ---------------------------------------------------------------------------
// Input choices that matter on a phone
// ---------------------------------------------------------------------------
//
//   * Font size is never below 16px on an input. Below that, iOS Safari zooms
//     the whole page on focus and the client has to pinch back out — which
//     reads as the site being broken.
//
//   * Currency uses type="text" with inputMode="decimal", NOT type="number".
//     type="number" brings spinner arrows nobody wants, scroll-wheel changes
//     that silently alter figures, and inconsistent decimal handling across
//     locales. We want the numeric keypad, not the number semantics.
//
//   * Yes/No/Not sure are three large buttons rather than a dropdown. 80 of
//     the ~93 questions a typical client sees are yes/no, so this is the
//     single most-used control in the product — it has to be one confident
//     thumb tap.
//
//   * Radio and select with four options or fewer render as tappable cards; a
//     longer list falls back to a native <select>, because the native picker
//     is genuinely better than anything we would build.

import type { AnswerValue, Question, RepeaterRow } from '@myshoebox/shared';
import { useId } from 'react';
import type { AttachmentSummary } from '../api/client.js';
import { AttachmentList } from './AttachmentList.js';
import { FileDrop } from './FileDrop.js';

export interface QuestionFieldProps {
  question: Question;
  value: AnswerValue | undefined;
  /** True when carried from last year and not yet confirmed. */
  isPrefilled: boolean;
  attachments: AttachmentSummary[];
  acceptedMimeTypes: string[];
  onChange: (value: AnswerValue) => void;
  onUpload: (files: File[]) => void;
  onDeleteAttachment: (id: string) => void;
  uploading: boolean;
  uploadProgress: number | undefined;
}

export function QuestionField(props: QuestionFieldProps): JSX.Element {
  const { question, value, isPrefilled, attachments } = props;
  const fieldId = useId();

  // A heading is explanatory text in the flow, not a question.
  if (question.type === 'heading') {
    return (
      <div className="q q--heading">
        <p>{question.label}</p>
      </div>
    );
  }

  // Evidence is revealed for a yes/no question only once the answer is yes or
  // unsure — the paper form's "please provide evidence if you tick YES",
  // enforced rather than requested. Asking a client who answered "no" to
  // attach something is noise.
  const showUpload =
    question.allowFiles && (question.type !== 'yesno' || value === 'yes' || value === 'unsure');

  return (
    <div className={`q ${isPrefilled ? 'q--prefilled' : ''}`}>
      {isPrefilled && (
        <p className="q__prefill">
          <span aria-hidden="true">↩</span> Last year you said this — check it is still right
        </p>
      )}

      <label className="q__label" htmlFor={fieldId}>
        {question.label}
        {question.required && (
          <span className="q__required" aria-label="required">
            {' '}
            *
          </span>
        )}
      </label>

      {question.help && <p className="q__help">{question.help}</p>}

      <Control {...props} fieldId={fieldId} />

      {showUpload && (
        <div className="q__evidence">
          <FileDrop
            onFiles={props.onUpload}
            hint={question.filesHint}
            accept={props.acceptedMimeTypes}
            disabled={props.uploading}
          />
          {props.uploading && props.uploadProgress !== undefined && (
            <output className="upload-progress">
              <div className="upload-progress__track">
                <div
                  className="upload-progress__fill"
                  style={{ width: `${Math.round(props.uploadProgress * 100)}%` }}
                />
              </div>
              <span>{Math.round(props.uploadProgress * 100)}%</span>
            </output>
          )}
          <AttachmentList items={attachments} onDelete={props.onDeleteAttachment} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The control for each type
// ---------------------------------------------------------------------------

function Control({
  question,
  value,
  onChange,
  fieldId,
}: QuestionFieldProps & { fieldId: string }): JSX.Element {
  switch (question.type) {
    case 'yesno':
      return <YesNo value={value} onChange={onChange} />;

    case 'radio':
      return <OptionCards question={question} value={value} onChange={onChange} />;

    case 'select':
      // Four or fewer reads better as cards; more is a job for the native
      // picker, which handles long lists and small screens far better.
      return (question.options?.length ?? 0) <= 4 ? (
        <OptionCards question={question} value={value} onChange={onChange} />
      ) : (
        <NativeSelect question={question} value={value} onChange={onChange} fieldId={fieldId} />
      );

    case 'multiselect':
      return <MultiSelect question={question} value={value} onChange={onChange} />;

    case 'repeater':
      return <Repeater question={question} value={value} onChange={onChange} />;

    case 'fileonly':
      // The upload area below the label is the whole control.
      return <></>;

    case 'longtext':
      return (
        <textarea
          id={fieldId}
          className="input input--multiline"
          value={typeof value === 'string' ? value : ''}
          placeholder={question.placeholder}
          rows={4}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    case 'currency':
      return (
        <div className="input-prefix">
          <span className="input-prefix__symbol" aria-hidden="true">
            $
          </span>
          <input
            id={fieldId}
            className="input input--prefixed"
            type="text"
            // Numeric keypad WITHOUT type="number": no spinners, no
            // scroll-to-change, no locale decimal surprises.
            inputMode="decimal"
            value={value === undefined || value === null ? '' : String(value)}
            placeholder={question.placeholder ?? '0.00'}
            onChange={(event) => onChange(event.target.value)}
          />
        </div>
      );

    case 'number':
      return (
        <input
          id={fieldId}
          className="input"
          type="text"
          inputMode="decimal"
          value={value === undefined || value === null ? '' : String(value)}
          placeholder={question.placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    case 'date':
      return (
        <input
          id={fieldId}
          className="input"
          type="date"
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value)}
        />
      );

    default:
      return (
        <input
          id={fieldId}
          className="input"
          // Pick the keyboard from what the question is asking for. A client
          // typing an email on a phone should get the @ key without hunting.
          type={inputTypeFor(question.code)}
          inputMode={inputModeFor(question.code)}
          autoComplete={autoCompleteFor(question.code)}
          value={typeof value === 'string' ? value : value === undefined ? '' : String(value)}
          placeholder={question.placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      );
  }
}

/**
 * Infer the right keyboard from the question code.
 *
 * Driven by the code rather than declared per question in the template,
 * because it is presentation rather than content — and because getting it from
 * the code means the 189 existing questions need no changes.
 */
function inputTypeFor(code: string): 'text' | 'email' | 'tel' {
  if (code === 'EMAIL' || code.endsWith('_EMAIL')) return 'email';
  if (code.startsWith('PHONE_') || code.includes('MOBILE')) return 'tel';
  return 'text';
}

function inputModeFor(code: string): 'text' | 'email' | 'tel' | 'numeric' {
  if (code === 'EMAIL' || code.endsWith('_EMAIL')) return 'email';
  if (code.startsWith('PHONE_') || code.includes('MOBILE')) return 'tel';
  // TFN, ABN, BSB and account numbers are digits, but keep them as text so a
  // leading zero survives and spaces are allowed.
  if (code === 'TFN' || code === 'ABN' || code.startsWith('BANK_')) return 'numeric';
  return 'text';
}

/**
 * Let the browser autofill the boring parts.
 *
 * A client doing this on their phone should not be typing their own address
 * and email by hand. Deliberately NOT set for the TFN or bank details — those
 * should not end up in a browser's autofill store.
 */
function autoCompleteFor(code: string): string | undefined {
  switch (code) {
    case 'FULL_NAME':
      return 'name';
    case 'EMAIL':
      return 'email';
    case 'PHONE_MOBILE':
      return 'tel';
    case 'ADDRESS':
      return 'street-address';
    case 'DOB':
      return 'bday';
    default:
      return undefined;
  }
}

// ---------------------------------------------------------------------------
// Yes / No / Not sure
// ---------------------------------------------------------------------------

const YESNO_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'unsure', label: 'Not sure' },
] as const;

function YesNo({
  value,
  onChange,
}: {
  value: AnswerValue | undefined;
  onChange: (value: AnswerValue) => void;
}): JSX.Element {
  return (
    <fieldset className="yesno">
      {YESNO_OPTIONS.map((option) => {
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            className={`yesno__button yesno__button--${option.value} ${selected ? 'is-on' : ''}`}
            aria-pressed={selected}
            // Tapping the selected answer clears it, so a mis-tap is one tap
            // to undo rather than a value the client cannot remove.
            onClick={() => onChange(selected ? '' : option.value)}
          >
            {option.label}
          </button>
        );
      })}
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

function OptionCards({
  question,
  value,
  onChange,
}: {
  question: Question;
  value: AnswerValue | undefined;
  onChange: (value: AnswerValue) => void;
}): JSX.Element {
  return (
    <fieldset className="options">
      {(question.options ?? []).map((option) => {
        const selected = String(value ?? '') === String(option.value);
        return (
          <button
            key={option.value}
            type="button"
            className={`option ${selected ? 'is-on' : ''}`}
            aria-pressed={selected}
            onClick={() => onChange(selected ? '' : option.value)}
          >
            <span className="option__marker" aria-hidden="true">
              {selected ? '✓' : ''}
            </span>
            <span>{option.label}</span>
          </button>
        );
      })}
    </fieldset>
  );
}

function NativeSelect({
  question,
  value,
  onChange,
  fieldId,
}: {
  question: Question;
  value: AnswerValue | undefined;
  onChange: (value: AnswerValue) => void;
  fieldId: string;
}): JSX.Element {
  return (
    <select
      id={fieldId}
      className="input input--select"
      value={String(value ?? '')}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">Choose…</option>
      {(question.options ?? []).map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function MultiSelect({
  question,
  value,
  onChange,
}: {
  question: Question;
  value: AnswerValue | undefined;
  onChange: (value: AnswerValue) => void;
}): JSX.Element {
  const chosen = Array.isArray(value) ? value.map(String) : [];
  return (
    <fieldset className="options">
      {(question.options ?? []).map((option) => {
        const selected = chosen.includes(String(option.value));
        return (
          <button
            key={option.value}
            type="button"
            className={`option option--multi ${selected ? 'is-on' : ''}`}
            aria-pressed={selected}
            onClick={() =>
              onChange(
                selected
                  ? chosen.filter((v) => v !== String(option.value))
                  : [...chosen, String(option.value)],
              )
            }
          >
            <span className="option__marker" aria-hidden="true">
              {selected ? '✓' : ''}
            </span>
            <span>{option.label}</span>
          </button>
        );
      })}
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// Repeater
// ---------------------------------------------------------------------------

/**
 * A repeating block: one per rental property, dividend, dependant.
 *
 * This replaces the paper form's fixed "1. 2. 3. 4." rows, which is why a
 * client with five children or three rentals is no longer stuck.
 */
function Repeater({
  question,
  value,
  onChange,
}: {
  question: Question;
  value: AnswerValue | undefined;
  onChange: (value: AnswerValue) => void;
}): JSX.Element {
  const rows: RepeaterRow[] = Array.isArray(value)
    ? (value.filter((row) => typeof row === 'object' && row !== null) as RepeaterRow[])
    : [];
  const fields = question.repeaterOf ?? [];

  const updateCell = (index: number, code: string, cellValue: string): void => {
    const next = rows.map((row, i) => (i === index ? { ...row, [code]: cellValue } : row));
    onChange(next);
  };

  return (
    <div className="repeater">
      {rows.map((row, index) => (
        // The index is a stable identity here: rows have no id of their own,
        // and reordering is not offered.
        // biome-ignore lint/suspicious/noArrayIndexKey: see above
        <div className="repeater__row" key={index}>
          <div className="repeater__head">
            <span className="repeater__number">{index + 1}</span>
            <button
              type="button"
              className="btn btn--ghost btn--small"
              onClick={() => onChange(rows.filter((_, i) => i !== index))}
            >
              Remove
            </button>
          </div>

          {fields.map((field) => (
            <div className="repeater__field" key={field.code}>
              {/* biome-ignore lint/a11y/noLabelWithoutControl: the control is
                  the sibling rendered immediately below. */}
              <label className="repeater__label">{field.label}</label>
              {field.type === 'longtext' ? (
                <textarea
                  className="input input--multiline"
                  rows={3}
                  value={String(row[field.code] ?? '')}
                  onChange={(event) => updateCell(index, field.code, event.target.value)}
                />
              ) : field.type === 'select' || field.type === 'yesno' ? (
                <select
                  className="input input--select"
                  value={String(row[field.code] ?? '')}
                  onChange={(event) => updateCell(index, field.code, event.target.value)}
                >
                  <option value="">Choose…</option>
                  {(field.type === 'yesno'
                    ? [
                        { value: 'yes', label: 'Yes' },
                        { value: 'no', label: 'No' },
                      ]
                    : (field.options ?? [])
                  ).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : field.type === 'currency' ? (
                <div className="input-prefix">
                  <span className="input-prefix__symbol" aria-hidden="true">
                    $
                  </span>
                  <input
                    className="input input--prefixed"
                    type="text"
                    inputMode="decimal"
                    value={String(row[field.code] ?? '')}
                    onChange={(event) => updateCell(index, field.code, event.target.value)}
                  />
                </div>
              ) : (
                <input
                  className="input"
                  type={field.type === 'date' ? 'date' : 'text'}
                  {...(field.type === 'number' ? { inputMode: 'decimal' as const } : {})}
                  value={String(row[field.code] ?? '')}
                  onChange={(event) => updateCell(index, field.code, event.target.value)}
                />
              )}
            </div>
          ))}
        </div>
      ))}

      <button
        type="button"
        className="btn btn--soft btn--block"
        onClick={() => onChange([...rows, {}])}
      >
        + Add {rows.length === 0 ? '' : 'another'}
      </button>
    </div>
  );
}
